import consumer from "./consumer"

const CHANNEL_IDENTIFIER = { channel: "Turbo::StreamsChannel" }
const DATASET_KEYS = {
  workerId: "currentWorkerId",
  streams: ["turboStreamGame", "turboStreamWorker", "turboStreamDepartment"]
}
const ATTRIBUTE_FILTER = [
  "data-current-worker-id",
  "data-turbo-stream-game",
  "data-turbo-stream-worker",
  "data-turbo-stream-department"
]

function safeRenderStream(data) {
  const filtered = filterMutedChatStream(data)
  if (!filtered) return

  const renderer = window?.Turbo?.renderStreamMessage
  if (typeof renderer === "function") {
    renderer.call(window.Turbo, filtered)
    return
  }
  console.warn("[TurboStreamsChannel] Turbo renderer missing; message skipped")
}

function mutedWorkerIdSet() {
  const candidates = Array.from(document.querySelectorAll("#chat-muted-worker-ids"))
  const sourceElement = candidates.length > 0 ? candidates[candidates.length - 1] : document.getElementById("chat-messages")
  const raw = sourceElement?.dataset?.mutedWorkerIds
  if (!raw) return new Set()

  return new Set(
    raw
      .split(",")
      .map((value) => String(value || "").replace(/\D/g, ""))
      .filter(Boolean)
  )
}

function filterMutedChatStream(data) {
  if (typeof data !== "string") return data
  if (!data.includes("chat-messages")) return data

  const muted = mutedWorkerIdSet()
  if (muted.size === 0) return data

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(data, "text/html")
    const streams = Array.from(doc.querySelectorAll("turbo-stream"))
    if (streams.length === 0) return data

    let removed = false

    streams.forEach((stream) => {
      if (stream.getAttribute("target") !== "chat-messages") return

      const template = stream.querySelector("template")
      const messageElement = template?.content?.querySelector?.('[data-role="chat-message"]')
      const workerId = messageElement?.getAttribute?.("data-worker-id")
      const normalizedWorkerId = String(workerId || "").replace(/\D/g, "")

      if (normalizedWorkerId && muted.has(normalizedWorkerId)) {
        stream.remove()
        removed = true
      }
    })

    if (!removed) return data

    const remaining = doc.body.innerHTML.trim()
    return remaining.length > 0 ? remaining : null
  } catch (_error) {
    // Fallback: best-effort regex check for a single chat message stream.
    const workerMatch = data.match(/data-worker-id=(?:"|&quot;)?(\d+)/)
    if (!workerMatch) return data
    const workerId = workerMatch[1]
    return muted.has(workerId) ? null : data
  }
}

function createStreamSubscription(streamName) {
  if (!streamName) return null

  return consumer.subscriptions.create(
    { ...CHANNEL_IDENTIFIER, signed_stream_name: streamName },
    { received: safeRenderStream }
  )
}

class TurboStreamManager {
  constructor(body) {
    this.body = body
    this.subscriptions = []
    this.observer = null
    this.signature = ""
  }

  init() {
    if (!this.body) return
    this.refresh()
    this.observe()
  }

  setBody(body) {
    if (this.body === body) return
    this.disconnectObserver()
    this.body = body
    if (body) {
      this.refresh()
      this.observe()
    }
  }

  refresh() {
    const workerId = this.datasetValue(DATASET_KEYS.workerId)
    const streamNames = DATASET_KEYS.streams
      .map((key) => this.datasetValue(key))
      .filter(Boolean)

    const nextSignature = [workerId, ...streamNames].join("|")
    if (nextSignature === this.signature) return
    this.signature = nextSignature

    this.unsubscribeAll()

    if (!workerId || streamNames.length === 0) return

    this.subscriptions = streamNames
      .map(createStreamSubscription)
      .filter(Boolean)
  }

  observe() {
    if (this.observer || !this.body) return

    this.observer = new MutationObserver((mutations) => {
      const shouldRefresh = mutations.some(
        (mutation) =>
          mutation.type === "attributes" &&
          ATTRIBUTE_FILTER.includes(mutation.attributeName)
      )

      if (shouldRefresh) {
        this.refresh()
      }
    })

    this.observer.observe(this.body, {
      attributes: true,
      attributeFilter: ATTRIBUTE_FILTER
    })
  }

  datasetValue(key) {
    return this.body?.dataset?.[key] || null
  }

  disconnectObserver() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  unsubscribeAll() {
    this.subscriptions.forEach((subscription) => subscription?.unsubscribe?.())
    this.subscriptions = []
  }

  disconnect({ preserveSubscriptions = false } = {}) {
    this.disconnectObserver()
    this.body = null
    if (!preserveSubscriptions) {
      this.unsubscribeAll()
      this.signature = ""
    }
  }
}

function startTurboStreams() {
  if (!document.body) return

  if (window.turboStreamManager) {
    window.turboStreamManager.setBody(document.body)
    return
  }

  const manager = new TurboStreamManager(document.body)
  manager.init()
  window.turboStreamManager = manager
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startTurboStreams)
} else {
  startTurboStreams()
}

document.addEventListener("turbo:load", startTurboStreams)

document.addEventListener("turbo:before-cache", () => {
  window.turboStreamManager?.disconnect({ preserveSubscriptions: true })
})
